import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// In-app customer support chat. Any signed-in customer can message support; replies from
// admin/staff appear here. Polls while open. Hidden on admin pages.
export const SupportChat = () => {
  const { pathname } = useLocation();
  const { isAuthed } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    if (!isAuthed) return;
    try { const { data } = await endpoints.supportThread(); setMessages(data.messages || []); setUnread(0); }
    catch { /* noop */ }
  }, [isAuthed]);

  // Poll unread badge in the background
  useEffect(() => {
    if (!isAuthed) { setUnread(0); return; }
    let t;
    const tick = async () => {
      if (!open) { try { const { data } = await endpoints.supportUnread(); setUnread(data.unread || 0); } catch { /* noop */ } }
      t = setTimeout(tick, 15000);
    };
    tick();
    return () => clearTimeout(t);
  }, [isAuthed, open]);

  // Poll thread while open
  useEffect(() => {
    if (!open || !isAuthed) return;
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [open, isAuthed, load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  if (pathname.startsWith("/admin")) return null;

  const openChat = () => {
    if (!isAuthed) { window.dispatchEvent(new Event("open-auth")); return; }
    setOpen(true);
  };

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const { data } = await endpoints.supportSend(t);
      setMessages((m) => [...m, data]);
      setText("");
    } catch { /* noop */ } finally { setSending(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={openChat} data-testid="support-chat-btn" aria-label="Chat with support"
          className="fixed right-4 bottom-44 z-[60] flex items-center justify-center w-12 h-12 rounded-full bg-[#2563EB] text-white shadow-lg shadow-black/40 hover:bg-[#1D4ED8] transition-colors">
          <MessageSquare className="w-5 h-5" />
          {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E11D2A] text-[10px] font-bold flex items-center justify-center" data-testid="support-unread-badge">{unread}</span>}
        </button>
      )}
      {open && (
        <div className="fixed right-4 bottom-24 z-[61] w-[calc(100vw-2rem)] max-w-sm rounded-2xl border border-[#273042] bg-[#0F131B] shadow-2xl flex flex-col overflow-hidden" data-testid="support-chat-panel">
          <div className="flex items-center justify-between px-4 h-14 bg-[#2563EB] text-white">
            <div className="flex items-center gap-2 font-bold"><MessageSquare className="w-5 h-5" /> Support chat</div>
            <button onClick={() => setOpen(false)} data-testid="support-close-btn" aria-label="Close support chat" className="p-1 hover:opacity-80"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 min-h-[280px] max-h-[50vh] overflow-y-auto p-3 space-y-2" data-testid="support-messages">
            {messages.length === 0 ? (
              <div className="text-center text-slate-500 text-sm py-10">Send us a message — our team replies here and on WhatsApp.</div>
            ) : messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === "customer" ? "bg-[#2563EB] text-white" : "bg-[#1A1F2B] text-slate-100 border border-[#273042]"}`}>
                  {m.sender === "staff" && <div className="text-[10px] text-[#93B4FF] font-semibold mb-0.5">{m.staffName || "Support"}</div>}
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-[#273042] flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your message…" data-testid="support-input"
              className="flex-1 min-w-0 h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white outline-none focus:border-[#2563EB]/60" />
            <button onClick={send} disabled={sending} data-testid="support-send-btn" aria-label="Send message"
              className="h-11 w-11 rounded-xl bg-[#2563EB] text-white flex items-center justify-center disabled:opacity-50">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
